import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "../../css/others/Table.css";
import Pagination from "../../Reusablecomponents/Pagination";
import { FiDownload, FiFileText, FiImage, FiFile } from "react-icons/fi";
import { AiOutlineFilePdf, AiOutlineFileExcel, AiOutlineFileWord } from "react-icons/ai";
import { BiSpreadsheet } from "react-icons/bi";
import { FaEye } from "react-icons/fa";
import { apiCall } from "../../main/ApiCall";
import { ViewAttachment, ViewChecklistDetails, TypeEnum } from '../../interFaces/interfaces';
import EncryptionUtils from "../../main/encryptionUtils";
const controlHeaders = [{ key: "pims", label: "PIMS" }, { key: "isms", label: "ISMS" }, { key: "bcms", label: "BCMS" }, { key: "cmmi", label: "CMMI" }, { key: "soc1", label: "SOC1" }, { key: "soc2", label: "SOC2" }, { key: "qms", label: "QMS" }, { key: "aims", label: "AIMS" }, { key: "erm", label: "ERM" }, { key: "itsm", label: "ITSM" }, { key: "ers", label: "ERS" }, { key: "ohs", label: "OHS" }];
const extensionToIcon: Record<string, JSX.Element> = {
  pdf: <AiOutlineFilePdf className="file-icon pdf" />,
  png: <FiImage className="file-icon image" />,
  jpg: <FiImage className="file-icon image" />,
  jpeg: <FiImage className="file-icon image" />,
  txt: <FiFileText className="file-icon text" />,
  csv: <BiSpreadsheet className="file-icon csv" />,
  xlsx: <AiOutlineFileExcel className="file-icon excel" />,
  docx: <AiOutlineFileWord className="file-icon word" />,
  default: <FiFile className="file-icon default" />,
};
const AuditorViewChecklistDetails: React.FC = () => {
  const { campaignId, projectId } = useParams();
  const [details, setDetails] = useState<ViewChecklistDetails | null>(null);
  const [, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [selectedAttachments, setSelectedAttachments] = useState<ViewAttachment[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [typeEnums, setTypeEnums] = useState<TypeEnum[]>([]);
  const [controlColumns, setControlColumns] = useState<string[]>([]);
  const [showPracticeAreaColumn, setShowPracticeAreaColumn] = useState<boolean>(true);
  const userInfo = EncryptionUtils.get<any>("userInfo", null);
  const roleName = EncryptionUtils.get("roleName");
  const userId = userInfo?.id;
  const createdBy = userInfo?.email;
  useEffect(() => {
    const fetchTypeEnums = async () => {
      try {
        const response = await apiCall("get", "/api/enums/TypeEnum");
        setTypeEnums(response);
      } catch (error) {
        console.error("Error fetching TypeEnum values:", error);
      }
    };
    fetchTypeEnums();
  }, []);
  useEffect(() => {
    const fetchChecklistDetails = async () => {
      if (!campaignId || !projectId) return;
      setLoading(true);
      try {
        const requestBody = { campaignId, projectId, createdBy, ...(roleName === "Auditor" && { auditorId: userId }) };
        const response = await apiCall("post", `/api/audit/campaign/auditor/questions`, requestBody, { page: currentPage - 1, size: itemsPerPage });
        if (!response || !response.responses) {
          setDetails({ content: [], totalElements: 0, totalPages: 0 });
          return;
        }
        const availableControls = controlHeaders
          .map(col => col.key)
          .filter(key =>
            response.responses.some((item: any) => item[key])
          );
        setControlColumns(availableControls);
        const hasControlFields = response.responses.some((item: any) =>
          controlHeaders.some(col => item[col.key])
        );
        setShowPracticeAreaColumn(!hasControlFields);
        const checklistData: ViewChecklistDetails = { content: response.responses.map((item: any) => ({ questionId: item.questionId, questionText: item.questionText || "Empty", practiceArea: item.practiceArea || "", typeOfFindings: item.typeOfFindings || "", findings: item.findings || "", attachments: item.attachments || [], pims: item.pims || "", isms: item.isms || "", bcms: item.bcms || "", cmmi: item.cmmi || "", soc1: item.soc1 || "", soc2: item.soc2 || "", qms: item.qms || "", aims: item.aims || "", erm: item.erm || "", itsm: item.itsm || "", ers: item.ers || "", ohs: item.ohs || "" })), totalElements: response.totalElements || 0, totalPages: response.totalPages || 0 };
        setDetails(checklistData);
        setTotalElements(checklistData.totalElements);
        setTotalPages(checklistData.totalPages);
      } catch (error) {
        console.error("Error fetching checklist details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchChecklistDetails();
  }, [campaignId, projectId, userId, createdBy, currentPage, itemsPerPage, roleName]);
  const openModal = (attachments: ViewAttachment[]) => {
    setSelectedAttachments(attachments);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setSelectedAttachments([]);
    setIsModalOpen(false);
  };
  const getTypeDescription = (typeValue: string) => {
    const typeEnum = typeEnums.find(enumItem => enumItem.value === typeValue);
    return typeEnum ? typeEnum.description : typeValue;
  };
  return (
    <div className="table-details">
      <div className="table-title">
        <h2>Audit Details</h2>
      </div>
      <div className="table-container">
        <div className="table">
          <table className="main-table">
            <thead>
              <tr>
                <th>S.No.</th>
                <th className="header-width">Question</th>
                {showPracticeAreaColumn && (
                  <th className="header-width">Control/Practice Area</th>
                )}
                <th className="header-width">Type of Findings</th>
                <th className="header-width">Findings</th>
                {controlHeaders
                  .filter(col => controlColumns.includes(col.key))
                  .map(col => (
                    <th key={col.key} className="header-width">{col.label}</th>
                  ))}
                <th className="header-width">Attachments</th>
              </tr>
            </thead>
            <tbody>
              {details?.content && details.content.length > 0 ? (
                details.content.map((item, index) => (
                  <tr key={item.questionId}>
                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td>{item.questionText || "Empty"}</td>
                    {showPracticeAreaColumn && (
                      <td>{item.practiceArea || "Empty"}</td>
                    )}
                    <td>{getTypeDescription(item.typeOfFindings ?? "") || "Empty"}</td>
                    <td>{item.findings || "Empty"}</td>
                    {controlHeaders
                      .filter(col => controlColumns.includes(col.key))
                      .map(col => {
                        const value = item[col.key as keyof typeof item];
                        return (
                          <td key={col.key}>
                            {typeof value === "string" || typeof value === "number" ? value : ""}
                          </td>
                        );
                      })}
                    <td>
                      {item.attachments.length > 0 ? (
                        <button onClick={() => openModal(item.attachments)} className="action-button"><FaEye /></button>
                      ) : (
                        <span className="not-applicable-text">No attachments</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7 + controlHeaders.filter(col => controlColumns.includes(col.key)).length + (showPracticeAreaColumn ? 1 : 0)} className="no-data">No questions available for this project</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination currentPage={currentPage} totalPages={totalPages} totalElements={totalElements} itemsPerPage={itemsPerPage} onPageChange={setCurrentPage} onItemsPerPageChange={setItemsPerPage} />
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Attachments</h2>
            <div className="attachment-container">
              {selectedAttachments.map((attachment, index) => {
                const fileName = attachment.fileKey || "unknown";
                const fileExtension = fileName.split(".").pop()?.toLowerCase() || "default";
                const icon = extensionToIcon[fileExtension] || extensionToIcon.default;
                return (
                  <div key={index} className="attachment-item">{icon}
                    <a href={attachment.presignedUrl} target="_blank" rel="noopener noreferrer" className="attachment-link">{fileName}</a>
                    <a href={attachment.presignedUrl} download={fileName} className="download-icon"><FiDownload /></a>
                  </div>
                );
              })}
              <button className="close" onClick={closeModal}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default AuditorViewChecklistDetails;